import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { ClipboardList, CheckCircle, Clock, Star, ChevronRight, Award } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import * as api from '../lib/api';

const WeeklySurveysPage = () => {
  const { user } = useAuth();
  const [surveys, setSurveys] = useState([]);
  const [stats, setStats] = useState({ available: 0, completed: 0, total_earned: '$0', streak: 0 });
  const [currentSurvey, setCurrentSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadSurveys(); }, [user]);

  const loadSurveys = async () => {
    try {
      const data = await api.getSurveys();
      setSurveys(data.surveys || []);
      setStats(data.stats || stats);
    } catch {
      setSurveys([
        { survey_id: '1', title: 'Weekly Work Experience Survey', description: 'Share feedback about your work experience this week', questions_count: 5, estimated_time: '3 min', reward: '$5', status: 'available', due_date: 'Due by Sunday', questions: [
          { id: 'q1', type: 'rating', text: 'How satisfied are you with your current project?', options: [1,2,3,4,5] },
          { id: 'q2', type: 'multiple', text: 'What best describes your workload?', options: ['Too light', 'Just right', 'Heavy', 'Too heavy'] },
          { id: 'q3', type: 'text', text: 'Any additional feedback?' }
        ]},
        { survey_id: '2', title: 'Platform Satisfaction Survey', description: 'Help us improve Duty AI', questions_count: 8, estimated_time: '5 min', reward: '$10', status: 'available', due_date: 'Due by Friday', questions: [
          { id: 'q1', type: 'rating', text: 'Overall platform satisfaction?', options: [1,2,3,4,5] },
        ]},
        { survey_id: '3', title: 'Skills Assessment Feedback', description: 'Rate your skills assessment experience', questions_count: 4, estimated_time: '2 min', reward: '$3', status: 'completed', completed_date: 'Completed Jan 25', questions: [] },
      ]);
      setStats({ available: 2, completed: 1, total_earned: '$3', streak: 8 });
    } finally { setLoading(false); }
  };

  const handleAnswer = (qId, val) => setAnswers({ ...answers, [qId]: val });

  const handleSubmit = async () => {
    try { await api.submitSurvey(currentSurvey.survey_id, answers); } catch {}
    toast({ title: 'Survey submitted!', description: `Thank you! Your reward of ${currentSurvey.reward} will be credited.` });
    setSurveys(surveys.map(s => s.survey_id === currentSurvey.survey_id ? { ...s, status: 'completed', completed_date: 'Just now' } : s));
    setCurrentSurvey(null); setAnswers({});
  };

  const available = surveys.filter(s => s.status === 'available');
  const completed = surveys.filter(s => s.status === 'completed');

  return (
    <div className="flex min-h-screen bg-[#F9FBF5]">
      <Sidebar />
      <div className="flex-1 ml-0 md:ml-[70px] pt-[60px] md:pt-0 pb-[60px] md:pb-0">
        <div className="max-w-5xl mx-auto px-8 py-12">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Weekly Surveys</h1>
            <p className="text-gray-600">Complete surveys to earn rewards and help improve the platform</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="p-6 text-center"><ClipboardList className="w-8 h-8 text-[#0A3900] mx-auto mb-2" /><div className="text-2xl font-bold text-gray-900 mb-1">{stats.available}</div><div className="text-sm text-gray-600">Available</div></Card>
            <Card className="p-6 text-center"><CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" /><div className="text-2xl font-bold text-gray-900 mb-1">{stats.completed}</div><div className="text-sm text-gray-600">Completed</div></Card>
            <Card className="p-6 text-center"><Award className="w-8 h-8 text-yellow-500 mx-auto mb-2" /><div className="text-2xl font-bold text-gray-900 mb-1">{stats.total_earned}</div><div className="text-sm text-gray-600">Total Earned</div></Card>
            <Card className="p-6 text-center"><Star className="w-8 h-8 text-orange-500 mx-auto mb-2" /><div className="text-2xl font-bold text-gray-900 mb-1">{stats.streak}</div><div className="text-sm text-gray-600">Streak (weeks)</div></Card>
          </div>

          {currentSurvey && (
            <Card className="p-8 mb-8 border-2 border-[#B8C4AD]">
              <div className="flex items-center justify-between mb-6">
                <div><h2 className="text-2xl font-bold text-gray-900">{currentSurvey.title}</h2><p className="text-gray-600 mt-1">{currentSurvey.description}</p></div>
                <Button variant="outline" onClick={() => { setCurrentSurvey(null); setAnswers({}); }}>Close</Button>
              </div>
              <div className="space-y-8">
                {(currentSurvey.questions || []).map((q, idx) => (
                  <div key={q.id} className="pb-6 border-b last:border-0">
                    <Label className="text-base font-medium text-gray-900 mb-3 block">{idx + 1}. {q.text}</Label>
                    {q.type === 'rating' && <div className="flex gap-2 mt-3">{q.options.map(o => (<button key={o} onClick={() => handleAnswer(q.id, o)} className={`w-12 h-12 rounded-lg border-2 flex items-center justify-center font-semibold transition-all ${answers[q.id] === o ? 'border-[#0A3900] bg-[#0A3900] text-white' : 'border-gray-300 text-gray-700 hover:border-[#3f8033]'}`}>{o}</button>))}</div>}
                    {q.type === 'multiple' && <div className="flex flex-wrap gap-2 mt-3">{q.options.map(o => (<button key={o} onClick={() => handleAnswer(q.id, o)} className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${answers[q.id] === o ? 'border-[#0A3900] bg-[#F1F5E6] text-[#0A3900]' : 'border-gray-300 text-gray-700 hover:border-[#3f8033]'}`}>{o}</button>))}</div>}
                    {q.type === 'text' && <Textarea className="mt-3" rows={3} placeholder="Type your response..." value={answers[q.id] || ''} onChange={(e) => handleAnswer(q.id, e.target.value)} />}
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t flex justify-end">
                <Button onClick={handleSubmit} className="bg-[#0A3900] hover:bg-[#1A5C0A] px-8">Submit Survey & Earn {currentSurvey.reward}</Button>
              </div>
            </Card>
          )}

          {!currentSurvey && !loading && (
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Available Surveys</h2>
              <div className="space-y-4 mb-10">
                {available.map(s => (
                  <Card key={s.survey_id} className="p-6 hover:shadow-md transition-shadow cursor-pointer" onClick={() => setCurrentSurvey(s)}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-[#E0E8D0] rounded-lg"><ClipboardList className="w-6 h-6 text-[#0A3900]" /></div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{s.title}</h3>
                          <p className="text-sm text-gray-600 mb-2">{s.description}</p>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="flex items-center gap-1 text-gray-500"><Clock className="w-4 h-4" />{s.estimated_time}</span>
                            <span className="text-gray-500">{s.questions_count} questions</span>
                            <Badge className="bg-green-100 text-green-800">{s.reward} reward</Badge>
                            <span className="text-orange-600 font-medium">{s.due_date}</span>
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </Card>
                ))}
                {available.length === 0 && <Card className="p-12 text-center"><CheckCircle className="w-16 h-16 text-green-300 mx-auto mb-4" /><h3 className="text-xl font-semibold text-gray-900 mb-2">All done!</h3><p className="text-gray-600">Check back next week!</p></Card>}
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Completed Surveys</h2>
              <div className="space-y-4">
                {completed.map(s => (
                  <Card key={s.survey_id} className="p-6 opacity-75">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-green-100 rounded-lg"><CheckCircle className="w-6 h-6 text-green-600" /></div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{s.title}</h3>
                        <p className="text-sm text-gray-600 mb-2">{s.description}</p>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-green-600 font-medium">{s.completed_date}</span>
                          <Badge className="bg-green-100 text-green-800">{s.reward} earned</Badge>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WeeklySurveysPage;
